import React, { useEffect, useState, useRef, useCallback } from 'react';
import styled from 'styled-components/macro';
import { compose } from 'ramda';
import { RouteComponentProps } from 'react-router-dom';
import {
  showToast,
  BackTo,
  Card,
  Button,
  Typography,
  PaymentsBalance,
  Modal,
  Tooltip,
} from '@deeptrue-frontend/ui';
import { ProjectStatus } from 'consts/statuses';
import { Dashboard } from 'components/Layout';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { OrganizationType } from 'store/modules/auth/auth';
import { withMetaDescription } from 'components/HeadProvider/HeadProvider';
import ProjectMenu from '../Common/ProjectMenu';
import { withProject, WithProjectProps } from '../withProject';
import api from 'api';
import { useFormatMessage } from 'components/IntlProvider';
import { ProductsReducerType } from 'store/modules/reports/reports.types';
import { usePaymentsLimits } from 'hooks';
import { ProductType } from 'api/resources/products/products.types';
import ProjectStatusNotification from '../Common/ProjectStatusNotification';
import CreditCardForm from 'components/Payments/CreditCardForm';
import DataCollectionLinkModal from './ManualDataCollection/DataCollectionLinkModal';
import env from 'consts/env';
import Loader from 'components/Loader/Loader';
import CompleteDataCollectionModal from 'components/Project/CollectData/CompleteDataCollectionModal';

const GenerateLinkWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
`;

const HiddenInput = styled.div<{ isHide: boolean }>`
  width: 50%;
  min-width: 200px;
  & input {
    height: 2.25rem;
    width: 100%;
  }
  ${({ isHide }) =>
    isHide &&
    `
 position: fixed;
  top: -100px;
   right: 0;
   z-index: -99999;
   width: 1px;
   height:1px;
  `}
`;

const PaymentWrapper = styled.div`
  margin-top: 1rem;
`;

const StyledButtonMargin = styled(Button)<{
  innerRef?: React.Ref<HTMLButtonElement>;
}>`
  margin-top: 1rem;
  padding: 0.25rem 2.5rem;

  &:disabled {
    color: #5c6370;
    background-color: #e3e5e8;
  }
`;

const StyledButton = styled(Button)`
  &:disabled {
    color: #5c6370;
    background-color: #e3e5e8;
  }
`;

const Container = styled.div`
  width: 100%;
  min-height: calc(100vh - 11.375rem);

  @media screen and (min-width: 960px) {
    display: grid;
    grid-template-columns: 1fr 408px;
    column-gap: 2rem;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 2rem 2rem;
  @media (min-width: 960px) {
    padding: 0;
  }
  margin-bottom: 2rem;
`;

const StyledCard = styled(Card)<{ disabled?: boolean }>`
  margin-top: 1.25rem;
  .card__subhead {
    font-size: 1rem;
    font-weight: 500;
  }

  ${({ disabled }) =>
    disabled &&
    `
  opacity: 0.5;
    `}
`;

const Header = styled(Typography)`
  font-weight: 500;
  font-size: 1.125rem;
  margin-bottom: 0.5rem;
  color: #253858;
  display: flex;
  align-items: center;

  & svg {
    margin-left: 0.5rem;
  }
`;

const SubHeader = styled(Typography)`
  color: #253858;
  margin-top: 0.375rem;
  font-size: 0.875rem;
  font-weight: 400;
`;

const Sidebar = styled.div`
  height: 100%;
  border-left: 1px solid ${({ theme }) => theme.colors.quillGray};
  background-color: transparent;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-right: -2rem;
`;

const Divider = styled.hr`
  margin: 1.5rem 0;
  border-top: 1px solid #e3e5e8;
  border-left: 1px solid #e3e5e8;
  width: calc(100% + 3rem);
  margin-left: -1.5rem;
`;

const Title = styled(Typography)`
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 2rem;
`;

const Subtitle = styled(Typography)`
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.25rem;
`;

const StyledModal = styled(Modal)`
  max-width: 40rem;
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 4px;
  .modal-header {
    border-bottom: 1px solid #e3e5e8;
    margin-bottom: 0;
    padding-bottom: 4rem;
    span {
      margin-top: 0.5rem;
    }
  }
`;

const ProjectMenuWrapper = styled.div`
  width: calc(100% - 2rem);
  .tab-bar {
    margin-left: -2rem;
    padding-left: 2rem;
    margin-right: -2rem;
    padding-right: 2rem;
  }
`;
const CompleteDataCollectionWrapper = styled.div`
  background-color: #f1f2f3;
  width: 100%;
  margin-top: 1rem;
  padding: 1rem;
  display: flex;
  align-items: center;
  border-radius: 4px;
`;

const CompleteDataCollectionContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const CompleteDataCollectionTitle = styled(Typography)`
  color: #253858;
  font-weight: 500;
  font-size: 0.875rem;
`;

const CompleteDataCollectionText = styled(Typography)`
  color: #253858;
  font-weight: 400;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const CompleteButton = styled(Button)`
  border: 1px solid #e3e5e8;
  min-width: fit-content;
  height: fit-content;
  margin-left: 3.3rem;
`;

const { connect, selectors, actions } = StoreProvider;

interface ProjectProps
  extends RouteComponentProps<{ id: string; codename: string }> {
  project: WithProjectProps['project'];
  organization: OrganizationType;
  isAdmin: boolean;
  reportsTables: ProductsReducerType['tables'];
  setReportsTables: (data: ProductsReducerType['tables']) => void;
  clearReportsTables: () => void;
  product: ProductType;
  updateProject: (project: WithProjectProps['project']) => void;
}

const ProjectCollectData = ({
  location,
  project,
  organization,
  match: { params },
  history,
  clearReportsTables,
  setReportsTables,
  updateProject,
}: ProjectProps) => {
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [successPath, setSuccessPath] = useState('');
  const [anonymousLink, setAnonymousLinkUrl] = useState('');
  const [notificationURL, setNotificationURL] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const anonymuosLinkRef = useRef<HTMLInputElement>(null);
  const { available_tokens } = usePaymentsLimits({ organization }).limitsRes;
  const { arePaymentInformationLoaded } = usePaymentsLimits({
    organization,
  });
  const t = useFormatMessage();
  const shouldDisplayCollectDataModal =
    project.status.value === ProjectStatus.OFFER_ACCEPTED;

  const hasDataCollectionStarted =
    project.status.value === ProjectStatus.COLLECTING_DATA;

  const [isDataCollectionModalEnabled, setIsDataCollectionModalEnabled] =
    useState(false);

  const [
    isCompleteDataCollectionModalEnabled,
    setIsCompleteDataCollectionModalEnabled,
  ] = useState(false);

  const {
    data_collection_method,
    id,
    status,
    is_provider_initiated,
    final_fee: totalProjectCostValue,
  } = project;

  const hasSufficientCredits =
    project.id !== -1 && available_tokens >= totalProjectCostValue;
  const tokensNeeded = totalProjectCostValue - available_tokens;

  const closeDataCollectionModal = () => setIsDataCollectionModalEnabled(false);

  const copyLinkToClipboard = () => {
    setTimeout(() => {
      anonymuosLinkRef.current?.select();
      document.execCommand('copy');
      showToast({
        type: 'success',
        title: `Link copied to clipboard`,
      });
    }, 300);
  };

  const generateLink = () => {
    if (shouldDisplayCollectDataModal)
      return setIsDataCollectionModalEnabled(true);

    copyLinkToClipboard();
  };

  const confirmCollectingData = () => {
    setIsProcessing(true);

    api.resources
      .collectProjectData({
        organizationCodename: params.codename || organization.codename,
        id: project.id,
      })
      .then(({ data }) => {
        updateProject(data);
        setIsProcessing(false);
        closeDataCollectionModal();
        copyLinkToClipboard();
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
        setIsProcessing(false);
      });
  };

  useEffect(() => {
    api.resources
      .getTables({
        organizationCodename: params.codename || organization.codename,
        projectUuid: project.uuid,
      })
      .then(({ data }) => {
        setReportsTables(data);
      });
    return () => {
      clearReportsTables();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  const isAutomaticDataCollectionEnable =
    status.value === ProjectStatus.OFFER_ACCEPTED ||
    (status.value === ProjectStatus.COLLECTING_DATA && !is_provider_initiated);

  const isAutomaticDataCollecionWithProvider =
    status.value === ProjectStatus.OFFER_ACCEPTED && is_provider_initiated;

  const isManualDataCollectionEnable =
    status.value === ProjectStatus.OFFER_ACCEPTED ||
    status.value === ProjectStatus.COLLECTING_DATA;

  const isCompleteDataCollectionEnable =
    status.value === ProjectStatus.COLLECTING_DATA &&
    data_collection_method === 2;

  const emailDataUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/${id}/collect-data/email`
    : `/project/${id}/collect-data/email`;
  const csvDataUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/${id}/collect-data/csv`
    : `/project/${id}/collect-data/csv`;
  const linkDataUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/${id}/collect-data/link`
    : `/project/${id}/collect-data/link`;
  const dataCollectionUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/${id}/collect-data/link`
    : `/project/${id}/collect-data`;

  const automaticDataUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/${id}/collect-data/automatic`
    : `/project/${id}/collect-data/automatic`;

  const reportUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/${id}/reports`
    : `/project/${id}/reports`;

  const isProjectDraft = status.value === ProjectStatus.OFFER_DRAFT;

  const isProductAllowManualDataCollection =
    Number(data_collection_method) === 3 ||
    Number(data_collection_method) === 2 ||
    data_collection_method === null;

  const isProductAllowAutomaticDataCollection =
    Number(data_collection_method) === 3 ||
    Number(data_collection_method) === 1 ||
    data_collection_method === null;

  useEffect(() => {
    if (isAutomaticDataCollecionWithProvider) {
      showToast({
        type: 'info',
        title: t(
          'project.collectData.toast.startedWithProvider',
          'Automatic data collection already started.'
        ),
      });
    }
    if (isProjectDraft) {
      showToast({
        type: 'info',
        title: t(
          'project.collectData.toast.draft',
          'You need to confirm your project to start data collection'
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const completeDataCollection = useCallback(async () => {
    const organizationName = params.codename
      ? params.codename
      : organization.codename;

    api.resources
      .finishDataCollection(organizationName, params.id)
      .then(() => {
        closeDataCollectionModal();
        updateProject(project);
        showToast({
          type: 'collectingData',
          title: t(
            'project.collectData.toast.completed',
            'Data collection has now been completed. '
          ),
        });
        history.push(reportUrl);
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (project.id !== -1 && organization.codename) {
      api.resources
        .getProjectSurveys({
          orgName: params.codename || organization.codename,
          uuid: project.uuid,
        })
        .then(({ data }) => {
          if (data.results.length) {
            const { uuid } = data.results[0];
            setAnonymousLinkUrl(`${env.APP_SURVEY_URL}/public/${uuid}`);
          }
          setNotificationURL(
            params.codename
              ? `/admin/organizations/${params.codename}/projects/${
                  project.id
                }/${
                  project.status.value === 13
                    ? `statistics`
                    : project.status.value === 11
                    ? `reports`
                    : ''
                }/`
              : `/project/${project.id}/${
                  project.status.value === 13
                    ? `statistics`
                    : project.status.value === 11
                    ? `reports`
                    : ''
                }/`
          );
        })
        .catch(() => {
          showToast({
            type: 'error',
            title: t(
              'errors.toast.general',
              'Something went wrong, please contact administrator'
            ),
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  const overviewUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/`
    : `/`;

  return (
    <Dashboard
      title={project.name || t('page.project', 'Project details')}
      location={location}
      isFullWidth
      combinedTitle={project.combinedTitle}
    >
      <BackTo onClick={() => history.push(overviewUrl)}>
        {t('project.backTo.overview', 'Back to overview')}
      </BackTo>
      <ProjectMenuWrapper>
        <ProjectMenu
          id={id}
          projectStatus={status}
          isProjectLoaded={project.id !== -1}
        />
      </ProjectMenuWrapper>
      {!arePaymentInformationLoaded ? (
        <Loader />
      ) : (
        <Container>
          <Content>
            <ProjectStatusNotification
              status={project.status.value}
              ctaAction={() => history.push(notificationURL)}
              isManualDataCollection={data_collection_method === 2}
            />
            <Title variant="h4" mt="2.31rem" color="#353531">
              {t('project.collectData.process.title', 'Data Collection')}
            </Title>
            <Subtitle variant="h6" mt="0.5rem" color="#353531">
              {isManualDataCollectionEnable
                ? t(
                    'project.collectData.manual.subtitle',
                    'Select the preferred source of questionnaire respondents for your project.'
                  )
                : t(
                    'project.collectData.automatic.subtitle',
                    'Start data collection by ordering respondents.'
                  )}
            </Subtitle>
            {isProductAllowAutomaticDataCollection && (
              <>
                <Title variant="h4" mt="2rem" fontSize="1.125rem">
                  {t(
                    'project.collectData.process.automatic.title',
                    'Contacts by deeptrue'
                  )}
                </Title>
                <StyledCard disabled={!isAutomaticDataCollectionEnable}>
                  <Header variant="h5" mb="0.5rem">
                    {t(
                      'project.collectData.process.automatic.header',
                      'deeptrue Using a Premium Access Panel'
                    )}
                    <Tooltip text="An “access panel” is a big number of consumers who are willing to participate in surveys. The access panel is representing the total adult population of a country." />
                  </Header>
                  <SubHeader>
                    {t(
                      'project.collectData.process.automatic.desc',
                      'We will look for suitable target persons for your project in quality access panels available worldwide.'
                    )}
                  </SubHeader>
                  <StyledButtonMargin
                    variant="primary"
                    disabled={!isAutomaticDataCollectionEnable}
                    onClick={
                      !hasSufficientCredits || !isAutomaticDataCollectionEnable
                        ? () => {
                            setIsPaymentModalOpen(true);
                            setSuccessPath(automaticDataUrl);
                          }
                        : () => history.push(automaticDataUrl)
                    }
                  >
                    {t(
                      'project.collectData.process.automatic.start',
                      'Order respondents'
                    )}
                  </StyledButtonMargin>
                </StyledCard>
              </>
            )}

            {isProductAllowManualDataCollection && (
              <>
                <Title variant="h4" mt="2rem" fontSize="1.125rem">
                  {t(
                    'project.collectData.process.manual.title',
                    'Your Own Contacts'
                  )}
                </Title>

                <StyledCard disabled={!isManualDataCollectionEnable}>
                  <Header variant="h5" mb="0.5rem">
                    {t(
                      'project.collectData.process.manual.email.title',
                      'Input of e-mail addresses'
                    )}
                  </Header>
                  <SubHeader>
                    {t(
                      'project.collectData.process.manual.email.desc',
                      'Manually enter your contacts for your project. Deeptrue will send out the survey invitations.'
                    )}
                  </SubHeader>
                  <StyledButtonMargin
                    variant="primary"
                    data-testid="manual_data_collection-select-button"
                    onClick={
                      !hasSufficientCredits && !hasDataCollectionStarted
                        ? () => {
                            setIsPaymentModalOpen(true);
                            setSuccessPath(emailDataUrl);
                          }
                        : () => history.push(emailDataUrl)
                    }
                  >
                    {t('project.collectData.emails', 'Add emails')}
                  </StyledButtonMargin>

                  <Divider />

                  <Header variant="h5" mb="0.5rem">
                    {t(
                      'project.collectData.process.manual.csv.title',
                      'Import e-mail addresses (CSV)'
                    )}
                  </Header>
                  <SubHeader>
                    {t(
                      'project.collectData.process.manual.csv.desc',
                      'Upload contacts for your project into deeptrue. Deeptrue will send out the survey invitations.'
                    )}
                  </SubHeader>
                  <StyledButtonMargin
                    variant="primary"
                    onClick={
                      !hasSufficientCredits && !hasDataCollectionStarted
                        ? () => {
                            setIsPaymentModalOpen(true);
                            setSuccessPath(csvDataUrl);
                          }
                        : () => history.push(csvDataUrl)
                    }
                  >
                    {t('project.collectData.csv', 'Upload CSV')}
                  </StyledButtonMargin>

                  <Divider />

                  <Header variant="h5" mb="0.5rem">
                    {t(
                      'project.collectData.process.manual.link.title',
                      'Download survey links'
                    )}
                  </Header>
                  <SubHeader>
                    {t(
                      'project.collectData.process.manual.link.desc',
                      'Get one individual link for each contact. Send them to your contacts on your own.'
                    )}
                  </SubHeader>
                  <StyledButtonMargin
                    variant="primary"
                    onClick={
                      !hasSufficientCredits && !hasDataCollectionStarted
                        ? () => {
                            setIsPaymentModalOpen(true);
                            setSuccessPath(linkDataUrl);
                          }
                        : () => history.push(linkDataUrl)
                    }
                  >
                    {t('project.collectData.link', 'Download link')}
                  </StyledButtonMargin>

                  <Divider />

                  <Header variant="h5" mb="0.5rem">
                    {t(
                      'project.collectData.process.manual.anonymousLink.title',
                      'Anonymous Link'
                    )}
                  </Header>
                  <SubHeader>
                    {t(
                      'project.collectData.process.manual.anonymousLink.desc',
                      "Get a re-usable link. Send it to your target persons on your own. This link will not track respondent's details."
                    )}
                  </SubHeader>
                  <GenerateLinkWrapper>
                    <HiddenInput isHide={shouldDisplayCollectDataModal}>
                      <input ref={anonymuosLinkRef} value={anonymousLink} />
                    </HiddenInput>
                    <StyledButton
                      // disabled={!isManualDataCollectionEnable}
                      variant={'primary'}
                      onClick={
                        !hasSufficientCredits && !hasDataCollectionStarted
                          ? () => {
                              setIsPaymentModalOpen(true);
                              setSuccessPath(dataCollectionUrl);
                            }
                          : () => generateLink()
                      }
                    >
                      {shouldDisplayCollectDataModal
                        ? t('project.collectData.generate', 'Generate link')
                        : t('project.collectData.copyLink', 'Copy Link')}
                    </StyledButton>
                  </GenerateLinkWrapper>
                </StyledCard>

                {isCompleteDataCollectionEnable && (
                  <CompleteDataCollectionWrapper>
                    <CompleteDataCollectionContent>
                      <CompleteDataCollectionTitle>
                        {t(
                          'dashboard.manual.complete.title',
                          'Do you want to complete Data Collection?'
                        )}
                      </CompleteDataCollectionTitle>
                      <CompleteDataCollectionText>
                        {t(
                          'dashboard.manual.complete.text',
                          `Survey data will no longer be collected and no further
                        respondents can be added. Already collected data will
                        still be available in the report.`
                        )}
                      </CompleteDataCollectionText>
                    </CompleteDataCollectionContent>
                    <CompleteButton
                      variant="secondary"
                      onClick={() =>
                        setIsCompleteDataCollectionModalEnabled(true)
                      }
                    >
                      {t(
                        'dashboard.manual.complete',
                        'Complete Data Collection'
                      )}
                    </CompleteButton>
                  </CompleteDataCollectionWrapper>
                )}
              </>
            )}
          </Content>
          <Sidebar>
            <PaymentWrapper>
              <PaymentsBalance
                title={t('project.testEdit.balance.title', 'Your balance')}
                creditsText={t('payments.table.credits', 'Credits')}
                tooltip={t(
                  'briefing.summary.tooltip.credits',
                  'To use the deeptrue tools, you must first purchase Credits. You can buy credits in the "Payments" section of the dashboard. Immediately after, you can enjoy all the tools!'
                )}
                isLoaded={arePaymentInformationLoaded}
                credits={available_tokens}
                noCredits={t(
                  'project.testEdit.balance.noCredits',
                  'You have no credits'
                )}
                cta={t('project.testEdit.balance.buy', 'Buy credits credits')}
                ctaAction={() => {
                  setIsPaymentModalOpen(true);
                }}
                insufficientBalance={!hasSufficientCredits}
                notification={t(
                  'payment.notification.insufficient.balance',
                  'Insufficient balance to begin Data Collection.'
                )}
                hasDataCollectionStarted={hasDataCollectionStarted}
              />
            </PaymentWrapper>
          </Sidebar>
        </Container>
      )}
      <StyledModal
        isVisible={isPaymentModalOpen}
        onBackdropClick={() => setIsPaymentModalOpen(false)}
        onCloseClick={() => setIsPaymentModalOpen(false)}
      >
        <CreditCardForm
          location={location}
          initialCredits={tokensNeeded}
          totalProjectCostValue={totalProjectCostValue}
          successPath={successPath}
          onCancel={() => setIsPaymentModalOpen(false)}
        />
      </StyledModal>
      <CompleteDataCollectionModal
        isVisible={isCompleteDataCollectionModalEnabled}
        onBackdropClick={() => setIsCompleteDataCollectionModalEnabled(false)}
        onCloseClick={() => setIsCompleteDataCollectionModalEnabled(false)}
        onCompleteDataCollection={() => completeDataCollection()}
      />
      <DataCollectionLinkModal
        isVisible={isDataCollectionModalEnabled}
        onBackdropClick={closeDataCollectionModal}
        onCloseClick={closeDataCollectionModal}
        credits={project.final_fee}
        onAcceptTerms={confirmCollectingData}
        isLoading={isProcessing}
        voucher={project.voucher}
      />
    </Dashboard>
  );
};

export default compose(
  connect(
    (state) => ({
      organization: selectors.authSelectors.userOrganization(state),
      isAdmin: selectors.authSelectors.isOrganizationAdmin(state),
      reportsTables: selectors.reports.tables(state),
    }),
    {
      setReportsTables: actions.reports.setReportsTables,
      clearReportsTables: actions.reports.clearReportsTables,
      updateProject: actions.projects.setProject,
    }
  ),
  withMetaDescription({ title: 'Dashboard projects' }),
  withProject(({ match, organization }: ProjectProps) => ({
    projectId: match.params.id,
    organizationCodename: match.params.codename || organization.codename,
  }))
)(ProjectCollectData);
